import jsPDF from "jspdf";
import "jspdf-autotable";

// Helper function to convert number to Roman numeral
const toRomanNumeral = (num) => {
  if (num === 0) return "0";
  if (num === 1) return "I";
  if (num === 2) return "II";
  if (num === 3) return "III";
  if (num === 4) return "IV";
  return num.toString();
};

// Helper function to extract division number from division text
const extractDivisionNumber = (divisionText) => {
  if (!divisionText) return null;
  // Match "Division I", "Division II", "Division III", "Division IV", "Division 0"
  // IMPORTANT: Check IV before I{1,4} to avoid matching "I" from "IV"
  const romanMatch = divisionText.match(/Division\s+(IV|III|II|I|0)/);
  if (romanMatch) {
    const roman = romanMatch[1];
    if (roman === "IV") return 4;
    if (roman === "III") return 3;
    if (roman === "II") return 2;
    if (roman === "I") return 1;
    if (roman === "0") return 0;
  }
  // Fallback for numeric divisions
  const numMatch = divisionText.match(/Division\s+(\d+)/);
  return numMatch ? parseInt(numMatch[1]) : null;
};

const generateTermResultsPDF = (results, termName, className) => {
  const doc = new jsPDF("landscape", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ====== MAIN HEADER ======
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("ST. MARY'S JUNIOR SEMINARY - VISIGA", pageWidth / 2, 12, {
    align: "center",
  });

  doc.setFontSize(12);
  doc.text("CLASS TERM RESULTS REPORT", pageWidth / 2, 18, {
    align: "center",
  });

  // ====== CLASS INFORMATION ======
  let y = 28;
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(15, y - 4, pageWidth - 15, y - 4);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TERM RESULTS SUMMARY", 15, y);

  y += 7;
  doc.setFontSize(10);

  const currentDate = new Date().toLocaleDateString();
  const info = [
    ["Class Name:", className || "N/A"],
    ["Term Name:", termName || "N/A"],
    ["Date Generated:", currentDate],
    ["Total Students:", (results?.length || 0).toString()],
  ];

  info.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, 20, y);
    doc.setFont("helvetica", "normal");
    doc.text(value || "N/A", 60, y);
    y += 6;
  });

  // ====== UTILITY FUNCTION ======
  const checkEmpty = (getValue) =>
    results.every((r) => {
      const val = getValue(r);
      return !val || val === "" || val === "N/A";
    });

  // ====== CALCULATE DIVISION SUMMARY ======
  const divisionCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
  results?.forEach((student) => {
    const divNum = extractDivisionNumber(student.division);
    if (divNum !== null && divNum >= 0 && divNum <= 4) {
      divisionCounts[divNum] = (divisionCounts[divNum] || 0) + 1;
    }
  });

  // Class GPA = average of student GPAs (only students with valid GPA / 7+ subjects)
  const validStudentGpas = (results || [])
    .map((student) => student.gpa)
    .filter((gpa) => gpa !== null && gpa !== undefined && !Number.isNaN(Number(gpa)));
  const classGpa =
    validStudentGpas.length > 0
      ? validStudentGpas.reduce((sum, gpa) => sum + Number(gpa), 0) / validStudentGpas.length
      : null;
  const formatGpa = (gpa) => {
    if (gpa === null || gpa === undefined || Number.isNaN(Number(gpa))) return "N/A";
    return Number(gpa).toFixed(4);
  };

  // ====== DISPLAY DIVISION SUMMARY RIGHT AFTER TERM RESULTS SUMMARY ======
  const isDivisionEmpty = checkEmpty((s) => s.division);
  if (!isDivisionEmpty) {
    y += 8; // Add some space after term info

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("DIVISION SUMMARY", 15, y);

    y += 6;

    // Create table for division summary - horizontal layout
    const divisionSummaryHeaders = [
      "Division",
      "Division I",
      "Division II",
      "Division III",
      "Division IV",
      "Division 0",
      "Class GPA",
    ];
    const divisionSummaryData = [
      [
        "Number of Students",
        divisionCounts[1] || 0,
        divisionCounts[2] || 0,
        divisionCounts[3] || 0,
        divisionCounts[4] || 0,
        divisionCounts[0] || 0,
        formatGpa(classGpa),
      ],
    ];

    doc.autoTable({
      head: [divisionSummaryHeaders],
      body: divisionSummaryData,
      startY: y,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 42, halign: "left", fontStyle: "bold" }, // "Number of Students" label
        1: { cellWidth: 30, halign: "center" }, // Division I
        2: { cellWidth: 30, halign: "center" }, // Division II
        3: { cellWidth: 30, halign: "center" }, // Division III
        4: { cellWidth: 30, halign: "center" }, // Division IV
        5: { cellWidth: 30, halign: "center" }, // Division 0
        6: { cellWidth: 30, halign: "center" }, // Class GPA
      },
    });

    // Update y position after the table
    y = doc.lastAutoTable.finalY + 5; // Space after division summary table
  }

  // ====== CALCULATE SUBJECT GRADE SUMMARY ======
  const subjectGradeCounts = {};
  const excludedSubjects = [
    "CHEMISTRY-1",
    "CHEMISTRY-2",
    "CHEMISTRY-3",
    "PHYSICS-1",
    "PHYSICS-2",
    "PHYSICS-3",
    "BIOLOGY-1",
    "BIOLOGY-2",
    "BIOLOGY-3",
    "HISTORY-1",
    "HISTORY-2",
    "GEOGRAPHY-1",
    "GEOGRAPHY-2",
    "MATHEMATICS-1",
    "MATHEMATICS-2",
    "ECONOMICS-1",
    "ECONOMICS-2",
  ];
  const excludedSubjectsUpper = excludedSubjects.map((s) => s.toUpperCase());

  // Helper function to check if subject has results
  const hasSubjectResults = (subj) => {
    if (!subj) return false;
    return subj.exam10Average > 0 || subj.exam40Average > 0 || subj.exam50Marks > 0;
  };

  results?.forEach((student) => {
    Object.entries(student?.subjects || {}).forEach(([subject, details]) => {
      const subjectUpper = subject.toUpperCase();
      if (excludedSubjectsUpper.includes(subjectUpper)) return;

      // Only count grades for subjects that have results
      if (!hasSubjectResults(details)) return;

      if (!subjectGradeCounts[subject]) {
        subjectGradeCounts[subject] = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, S: 0 };
      }

      const grade = details?.grade;
      if (grade && ["A", "B", "C", "D", "E", "F", "S"].includes(grade.toUpperCase())) {
        const gradeUpper = grade.toUpperCase();
        if (subjectGradeCounts[subject][gradeUpper] !== undefined) {
          subjectGradeCounts[subject][gradeUpper]++;
        }
      }
    });
  });

  // ====== SUBJECT EXTRACTION ======
  const subjectSet = new Set();
  results?.forEach((student) => {
    Object.keys(student?.subjects || {}).forEach((sub) => subjectSet.add(sub));
  });

  // Order subjects nicely
  const subjectOrder = [
    'CIVICS', 'HISTORY','HISTORIA YA TZ NA MAADILI', 'GEOGRAPHY','BIBLE-KNOWLEDGE','KISWAHILI', 'ENGLISH LANGUAGE','ENGLISH',
      'PHYSICS', 'CHEMISTRY','BIOLOGY','ECONOMICS','BASIC MATHEMATICS','MATHEMATICS', 'GENERAL STUDIES', 'ADVANCED MATHEMATICS',
     'ACADEMIC COMMUNICATION', 'BUSINESS STUDIES' 
  ];

  const subjectNames = Array.from(subjectSet).sort((a, b) => {
    const A = a.toUpperCase();
    const B = b.toUpperCase();
    const indexA = subjectOrder.indexOf(A);
    const indexB = subjectOrder.indexOf(B);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return A.localeCompare(B);
  });

  // Display names (vertical headers)
  const subjectDisplayNames = subjectNames.map((name) => {
    const upper = name.toUpperCase();
    if (upper === "ENGLISH LANGUAGE") return "ENG-LANG";
    if (upper === "ADVANCED MATHEMATICS") return "A-MATH";
    if (upper === "GENERAL STUDIES") return "G/STUDIES";
    if (upper === "ACADEMIC COMMUNICATION") return "A-COMM";
    if (upper === "HISTORIA YA TZ NA MAADILI") return "HIST-TZ";
    if (upper === "GEOGRAPHY") return "GEO";
    if (upper === "HISTORY") return "HIST";
    if (upper === "ENGLISH") return "ENG";
    if (upper === "KISWAHILI") return "KISW";
    if (upper === "ECONOMICS") return "ECO";
    if (upper === "CIVICS") return "CIV";
    if (upper === "PHYSICS") return "PHY";
    if (upper === "CHEMISTRY") return "CHEM";
    if (upper === "BIOLOGY") return "BIO";
    if (upper === "BASIC MATHEMATICS") return "B-MATH";
    if (upper === "BIBLE-KNOWLEDGE") return "B-KNOW";
    if (upper === "BUSINESS STUDIES") return "B-STUDIES";
    return name;
  });

  // ====== COLUMN VISIBILITY CHECKS ======
  const isIndexEmpty = checkEmpty((s) => s.studentNumber);
  const isCombinationEmpty = checkEmpty((s) => s.combination);
  const isDivisionEmptyForTable = isDivisionEmpty;

  // ====== HEADERS ======
  const headers = ["No", "Student Name"];
  if (!isIndexEmpty) headers.push("Index No");
  if (!isCombinationEmpty) headers.push("Comb");
  headers.push(...subjectDisplayNames);
  headers.push("Total", "Avg");
  if (!isDivisionEmptyForTable) headers.push("Div");
  headers.push("Position");

  // ✅ used for Avg highlighting
  const avgColIndex = headers.indexOf("Avg");

  // ====== SUBJECT MARKS FUNCTION ======
  const getSubjectMarks = (subj) => {
    if (!subj) return "";
    if (subj.totalMarks) return subj.totalMarks;
    if (subj.exam50Marks) return subj.exam50Marks;
    return "";
  };

  // ====== ROWS ======
  const tableData = results.map((student, index) => {
    const row = [index + 1, student.studentName || ""];

    if (!isIndexEmpty) row.push(student.studentNumber || "");
    if (!isCombinationEmpty) row.push(student.combination || "");

    subjectNames.forEach((subject) => {
      const subj = student?.subjects?.[subject];
      row.push(getSubjectMarks(subj));
    });

    // ✅ do not use "||" here, to preserve 0 values
    row.push(student.totalMarks ?? "");
    row.push(student.average ?? "");

    if (!isDivisionEmptyForTable) {
      const divisionText = student.division || "";

      // Extract division Roman numeral and points
      const divisionMatch = divisionText.match(
        /Division\s+(IV|III|II|I|0)\s*•\s*([\d\.]+)/
      );

      if (divisionMatch) {
        const divisionRoman = divisionMatch[1];
        const points = divisionMatch[2];
        const cleanPoints = points.replace(/\.0$/, "");
        row.push(`Div ${divisionRoman}.${cleanPoints}`);
      } else {
        const simpleDivisionMatch = divisionText.match(/Division\s+(IV|III|II|I|0)/);
        if (simpleDivisionMatch) {
          row.push(`Div ${simpleDivisionMatch[1]}`);
        } else {
          const numericMatch = divisionText.match(/Division\s+(\d+)\s*•\s*([\d\.]+)/);
          if (numericMatch) {
            const divNum = parseInt(numericMatch[1]);
            const points = numericMatch[2];
            const cleanPoints = points.replace(/\.0$/, "");
            row.push(`Div ${toRomanNumeral(divNum)}.${cleanPoints}`);
          } else {
            row.push(divisionText);
          }
        }
      }
    }

    row.push(student.positionText || "");
    return row;
  });

  // ====== COLUMN WIDTHS (scaled for A4) ======
  const fixedWidths = {
    No: 8,
    "Student Name": 24,
    Total: 11,
    Avg: 11,
    Position: 12,
  };

  if (!isIndexEmpty) fixedWidths["Index No"] = 15;
  if (!isCombinationEmpty) fixedWidths["Comb"] = 11;
  if (!isDivisionEmptyForTable) fixedWidths["Div"] = 15;

  subjectDisplayNames.forEach((s) => {
    fixedWidths[s] = 8;
  });

  // ====== TABLE ======
  doc.autoTable({
    head: [headers],
    body: tableData,
    startY: y + 8,
    margin: { left: 8, right: 8, top: 5, bottom: 25 },

    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      overflow: "linebreak",
      halign: "center",
      valign: "middle",
    },

    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 7,
    },

    bodyStyles: {
      fontSize: 7,
      textColor: 0,
    },

    columnStyles: Object.fromEntries(
      Object.keys(fixedWidths).map((k) => [k, { cellWidth: fixedWidths[k], halign: "center" }])
    ),

    // ✅ UPDATED: keep vertical subject headers + highlight Avg < 60
    didParseCell: (data) => {
      // Vertical headers for subject columns
      if (data.section === "head" && subjectDisplayNames.includes(data.column.raw)) {
        data.cell.text = [];
      }

      // ✅ Highlight only AVG column when avg < 60
      if (data.section === "body" && avgColIndex !== -1 && data.column.index === avgColIndex) {
        const avgVal = parseFloat(data.cell.raw);
        if (!isNaN(avgVal) && avgVal < 60) {
          data.cell.styles.fillColor = [255, 200, 200]; // light red background
          data.cell.styles.textColor = [150, 0, 0]; // dark red text
          data.cell.styles.fontStyle = "bold";
        }
      }
    },

    didDrawCell: (data) => {
      // Draw vertical header text for subject columns
      if (data.section === "head" && subjectDisplayNames.includes(data.column.raw)) {
        const { x, y, width, height } = data.cell;
        doc.setFontSize(6);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text(data.column.raw, x + width / 2, y + height / 2, {
          angle: 90,
          align: "center",
          baseline: "middle",
        });
      }

      // Color code division cells based on division value
      const divColIndex = headers.indexOf("Div");
      if (data.section === "body" && divColIndex !== -1 && data.column.index === divColIndex) {
        const divisionValue = data.cell.text?.[0];
        if (divisionValue) {
          const { x, y, width, height } = data.cell;

          let fillColor;
          let textColor = [0, 0, 0];

          // IMPORTANT: Check IV before I to avoid matching "I" from "IV"
          if (divisionValue.includes("Div IV") && !divisionValue.includes("Div I")) {
            fillColor = [255, 200, 200];
            textColor = [180, 0, 0];
          } else if (divisionValue.includes("Div III") && !divisionValue.includes("Div IV")) {
            fillColor = [255, 235, 180];
            textColor = [180, 100, 0];
          } else if (
            divisionValue.includes("Div II") &&
            !divisionValue.includes("Div III") &&
            !divisionValue.includes("Div IV")
          ) {
            fillColor = [200, 220, 255];
            textColor = [0, 80, 180];
          } else if (
            divisionValue.includes("Div I") &&
            !divisionValue.includes("Div II") &&
            !divisionValue.includes("Div III") &&
            !divisionValue.includes("Div IV")
          ) {
            fillColor = [200, 255, 200];
            textColor = [0, 120, 0];
          } else if (divisionValue.includes("Div 0")) {
            fillColor = [240, 240, 240];
            textColor = [100, 100, 100];
          }

          if (fillColor) {
            doc.setFillColor(...fillColor);
            doc.rect(x, y, width, height, "F");

            doc.setFontSize(7);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...textColor);
            doc.text(divisionValue, x + width / 2, y + height / 2, {
              align: "center",
              baseline: "middle",
            });
          }
        }
      }
    },

    // FOOTER
    didDrawPage: () => {
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      const totalPages = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(120, 120, 120);
      doc.text(`Page ${currentPage} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
        align: "center",
      });
    },
  });

  // ====== ADD SUMMARY SECTIONS ======
  const finalY = doc.lastAutoTable.finalY || pageHeight - 30;
  let summaryY = finalY + 5;

  // Division Legend
  if (!isDivisionEmpty) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("Division Legend:", 15, summaryY);

    summaryY += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    const classType = results[0]?.classType || "O-LEVEL";

    if (classType === "A-LEVEL") {
      doc.text(
        "Div I.1-9 = Best, Div II.10-12 = Good, Div III.13-17 = Average, Div IV.18-21 = Fail, Div 0 = Ungraded-fail",
        15,
        summaryY
      );
    } else {
      doc.text(
        "Div I.1-17 = Best, Div II.18-21 = Good, Div III.22-25 = Average, Div IV.26-33 = fail, Div 0 = Ungraded-fail",
        15,
        summaryY
      );
    }

    summaryY += 10;
  }

  // Subject Grade Summary
  const subjectNamesForSummary = Object.keys(subjectGradeCounts).sort();
  if (subjectNamesForSummary.length > 0) {
    if (summaryY > pageHeight - 40) {
      doc.addPage();
      summaryY = 20;
    }

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("SUBJECT GRADE SUMMARY", 15, summaryY);

    summaryY += 6;

    const gradeSummaryHeaders = ["Subject", "A", "B", "C", "D", "E", "F", "S"];
    const gradeSummaryData = subjectNamesForSummary.map((subject) => {
      const counts = subjectGradeCounts[subject];
      return [subject, counts.A || 0, counts.B || 0, counts.C || 0, counts.D || 0, counts.E || 0, counts.F || 0, counts.S || 0];
    });

    doc.autoTable({
      head: [gradeSummaryHeaders],
      body: gradeSummaryData,
      startY: summaryY,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 6,
        cellPadding: 1.5,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 6,
      },
      bodyStyles: {
        fontSize: 6,
        textColor: 0,
      },
      columnStyles: {
        0: { cellWidth: 40, halign: "left" },
      },
    });

    // ====== SUBJECT AVERAGE (below SUBJECT GRADE SUMMARY) ======
    summaryY = doc.lastAutoTable.finalY + 8;

    if (summaryY > pageHeight - 40) {
      doc.addPage();
      summaryY = 20;
    }

    // Horizontal layout: subjects as columns; Average / Students as rows
    const shortSubjectName = (name) => {
      const upper = name?.toUpperCase?.() || "";
      if (upper === "ADVANCED MATHEMATICS") return "A-MATH";
      if (upper === "ENGLISH LANGUAGE") return "ENG-LANG";
      if (upper === "HISTORIA YA TZ NA MAADILI") return "HIST-TZ";
      if (upper === "GENERAL STUDIES") return "G/STUDIES";
      if (upper === "ACADEMIC COMMUNICATION") return "A/COMM";
      if (upper === "GEOGRAPHY") return "GEO";
      if (upper === "HISTORY") return "HIST";
      if (upper === "PHYSICS") return "PHY";
      if (upper === "CHEMISTRY") return "CHEM";
      if (upper === "BIOLOGY") return "BIO";
      if (upper === "ECONOMICS") return "ECO";
      if (upper === "BASIC MATHEMATICS") return "B-MATH";
      if (upper === "BIBLE-KNOWLEDGE") return "B-KNOW";
      if (upper === "BUSINESS STUDIES") return "B-STUDIES";
      if (upper === "MATHEMATICS") return "MATH";
      if (upper === "KISWAHILI") return "KISW";
      return name || "";
    };

    const subjectAvgAverages = [];
    const subjectAvgCounts = [];
    const subjectAvgHeaders = subjectNamesForSummary.map((subject) => {
      let total = 0;
      let count = 0;

      results?.forEach((student) => {
        const details = student?.subjects?.[subject];
        if (!hasSubjectResults(details)) return;

        const marks = Number(details?.totalMarks ?? details?.exam50Marks);
        if (!Number.isNaN(marks) && marks > 0) {
          total += marks;
          count += 1;
        }
      });

      subjectAvgAverages.push(count > 0 ? (total / count).toFixed(2) : "N/A");
      subjectAvgCounts.push(count);
      return shortSubjectName(subject);
    });

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("SUBJECT AVERAGE", 15, summaryY);

    summaryY += 6;

    doc.autoTable({
      head: [["Subject", ...subjectAvgHeaders]],
      body: [
        ["Average", ...subjectAvgAverages],
        ["Students", ...subjectAvgCounts],
      ],
      startY: summaryY,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 6,
        cellPadding: 1.5,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 6,
      },
      bodyStyles: {
        fontSize: 6,
        textColor: 0,
      },
      columnStyles: {
        0: { cellWidth: 22, halign: "left", fontStyle: "bold" },
      },
    });
  }

  // ====== SAVE ======
  const fileName = `Term_Results_${className}_${termName}.pdf`;
  doc.save(fileName);
};

export default generateTermResultsPDF;
